/**
 * DASHBOARD-HEATMAP.WIDGET
 * Display results in heatmap
 */
import SurveyQuestionLabel from "@/components/survey-question-label"
import Tooltip from "@/components/tooltip"
import QuestionBenchmarkModal from "@/features/benchmark/entities/question/ui/modals/QuestionBenchmarkModal"
import { withTranslation, WithTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"
import { useEffect, useMemo, useRef, useState } from "react"
import {
  DashboardFilters,
  DEFAULT_FILTERS,
  DEFAULT_RANDOM_ATTRIBUTES,
  Filter,
  FilterLite,
  FilterState
} from "@/redux/filter.types"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING, STATUS_LOAD_ERROR } from "@/redux/_status.types"
import NoResultsWidget from "./no-results.widget"
import { store } from "@/index"
import { filterReplaceDashboard, filterStatus } from "@/redux/filter.actions"
import { fetchAttributes, fetchHeatmap } from "@/redux/_archive.actions"
import ListItem from "@/components/list-item"
import Chip from "@/components/chip"
import { flatten, isEqual, max, mean, min, orderBy, uniq } from "lodash"
import PageLoader from "@/components/page-loader"
import Space from "@/components/space"
import { TopicState } from "@/redux/topic.types"
import { SurveyState } from "@/redux/survey.types"
import { generateHeatmap } from "@/redux/_generate.actions"
import { Axis, AxisState } from "@/redux/axis.types"
import PagePlaceholder from "@/components/page-placeholder"
import ContextMenu from "@/components/context-menu"
import initFilters from "@/utils/init-filters.utils"
import { BookmarkState } from "@/redux/bookmark.types"
import { NavigateFunction, useNavigate } from "react-router-dom"
import LoadingModal from "@/modals/loading.modal"
import { USER_LIST_GENDER } from "@/redux/user.types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBan,
  faCog,
  faFilter,
  faSortAmountDownAlt,
  faSortAmountUp,
  faUndo,
  faMagnifyingGlassChart
  // faCloud,    // commented with gollac gap column
  // faCloudBolt, // commented with gollac gap column
  // faSun        // commented with gollac gap column
} from "@fortawesome/free-solid-svg-icons"
import { Session } from "@/redux/_session.types"
import Card from "@/components/card"
import ListDropdown from "@/components/list-dropdown"
import ListButton from "@/components/list-button"
import HeatmapHelp from "@/help/heatmap.help"
import Modal from "@/components/modal"
import DashboardFiltersWidget from "./dashboard-filters.widget"
import getUserFilterDates, { UserFilterDates } from "@/utils/get-user-filter-dates.utils"
import getHeatmapCeils from "@/utils/get-heatmap-ceils.utils"
import { AttributeState } from "@/redux/attribute.types"
import { ProjectState } from "@/redux/project.types"
import Button from "@/components/button"
import { ACCOUNT_RESULTS_TYPES, AccountHeatmapResult } from "@/redux/account.types"
import { reportEdit } from "@/redux/report.actions"
import { axisFindTopicAid } from "@/redux/axis.actions"
import { selectSession } from "@/features/adm/entities/session/model/session.selector"
import { benchmarkFetchQuestions } from "@/redux/benchmark.actions"
import DashboardBenchmarkMiniCard from "@/features/benchmark/entities/question/ui/DashboardBenchmarkMiniCard"
import type { BenchmarkEntry } from "@/features/benchmark/entities/question/model/benchmarkQuestion.context"
import {
  buildBenchmarkByUuid,
  benchmarkDelta
} from "@/features/benchmark/entities/question/model/benchmarkQuestion.utils"
import BenchmarkDetailModal from "@/features/benchmark/entities/question/ui/BenchmarkDetailModal"
import { selectCanSeeBenchmark } from "@/features/benchmark/entities/question/model/benchmarkQuestion.selector"
import { useAppSelector } from "@/core/root-reducer"

interface StateProps {
  _session: Session
  attribute: AttributeState
  axis: AxisState
  bookmark: BookmarkState
  filter: FilterState
  survey: SurveyState
  project: ProjectState
  topic: TopicState
}

interface OwnProps {
  id?: string
  filterName?: string | null
  resultsType?: string | null
  isPreview?: boolean
  limit?: number
  offset?: number
  isGollac?: boolean
  isReport?: boolean
  hideHeader?: boolean
  skipFirstRow?: boolean
}

type Props = StateProps & OwnProps & WithTranslation

//Index of first value item in response array (column)
const FIRST_COLUMN_INDEX = 1

//Index of first value item in response array (line)
const FIRST_LINE_INDEX = 2

const MODAL_FILTERS = "MODAL_FILTERS"
const MODAL_LOADING = "MODAL_LOADING"
const MODAL_HELP = "MODAL_HELP"

function DashboardHeatmapWidget(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()
  const session = useSelector(selectSession)
  const canSeeBenchmark = useAppSelector(selectCanSeeBenchmark)

  //Columns to display
  const [columns, setColumns] = useState<string[]>([])

  //Length of the column text
  const [columnsTextLength, setColumnsTextLength] = useState<number>(0) //If text too long hide images

  //Data to display in lines
  const [data, setData] = useState<any[]>([])

  // search / filter / sort heatmap
  const [searchTerm, setSearchTerm] = useState("")
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(!props.isGollac ? "desc" : "asc")
  const [activeColumnIndex, setActiveColumnIndex] = useState<number | string | null>(!props.isGollac ? "firstCol" : 1)

  //Participation
  const [participations, setParticipations] = useState<any[]>([])

  //Wait for location change to load
  const [isLoading, setIsLoading] = useState<boolean>(true)

  //Scale
  const [scale, setScale] = useState(1)

  //Colors for heatmap
  const [colorRed] = useState(
    session.dashboardDisplaySettings.dashboardNightmareModeHeatmap
      ? session.dashboardDisplaySettings.dashboardNightmareColor1
      : "#f1a8a3"
  )
  const [colorGreen] = useState(
    session.dashboardDisplaySettings.dashboardNightmareModeHeatmap
      ? session.dashboardDisplaySettings.dashboardNightmareColor3
      : "#99efc2"
  )

  //Is cell open and position in the grid of the current cell
  const [currentCellOpen, setCurrentCellOpen] = useState([false, 0, 0])

  //Filter active
  const [currentFilter, setCurrentFilter] = useState<Filter>(initCurrentFilter())

  //Current modal
  const [currentModal, setCurrentModal] = useState<string | null>(null)

  //Type of data the query
  const [currentResultsType, setCurrentResultsType] = useState<AccountHeatmapResult>(initCurrentResultsType())

  //List of filters (contact default and custom)
  const [filters] = useState(initFilters())

  //Width of the first column (depends of the number of columns)
  const [firstColumnWidth, setFirstColumnWidth] = useState<number>(0)

  //Display company values
  const [showCompany, setShowCompany] = useState<boolean>(false)

  //Show population
  const [showPopulation, setShowPopulation] = useState<boolean>(false)

  //Benchmark column
  const [showBenchmark, setShowBenchmark] = useState<boolean>(false)
  const [benchmarkSectorLabel, setBenchmarkSectorLabel] = useState<string>("")
  //Fetched once per mount, then reused by every reload (filter, results type)
  const bmResponseRef = useRef<any>(null)

  //Level 3 supervisor (CS connected as the client): can give a question without benchmark one,
  //straight from its dash in the benchmark column
  const canCreateBenchmark = props._session.connectedAsSupervisor && props._session.supervisorLevel === 3
  const [benchmarkCreateQuestion, setBenchmarkCreateQuestion] = useState<{ id: string; text: string } | null>(null)

  //Benchmark detail modal
  const [hmDetailOpen, setHmDetailOpen] = useState(false)
  const [hmDetailData, setHmDetailData] = useState<{
    score: number
    axisColor: string
    question: string
    topicLabel: string
    questionAid: string | null
    benchmarkEntry: BenchmarkEntry | null
  } | null>(null)

  const hmNavItems = useMemo(
    () =>
      data
        .filter((item) => {
          const bm = item.values.find((v: any) => v.isBenchmark)
          return bm && bm.note != null && bm.canonicalAid
        })
        .map((item) => {
          const bm = item.values.find((v: any) => v.isBenchmark)
          const score = item.liveNote ?? item.companyValue
          return {
            label: item.label,
            topicLabel: item.headerSubtitle || "",
            axisLabel: item.headerLabel || "",
            questionAid: bm.canonicalAid as string,
            axisColor: item.headerColor || "#9CA3AF",
            delta: score != null && bm.note != null ? benchmarkDelta(score, bm.note) : undefined
          }
        }),
    [data]
  )

  const hmNavIndex = hmNavItems.findIndex((n) => n.questionAid === hmDetailData?.questionAid)

  function hmNavigateTo(index: number) {
    if (index < 0 || index >= hmNavItems.length) return
    const nav = hmNavItems[index]
    const dataItem = data.find((d) => {
      const bm = d.values.find((v: any) => v.isBenchmark)
      return bm && bm.canonicalAid === nav.questionAid
    })
    if (!dataItem) return
    setHmDetailData(buildHmDetailData(dataItem, dataItem.values.find((v: any) => v.isBenchmark)))
  }

  //item = heatmap row, bm = its isBenchmark value cell
  function buildHmDetailData(item: any, bm: any) {
    return {
      score: item.liveNote ?? item.companyValue,
      axisColor: item.headerColor || "#9CA3AF",
      question: item.label,
      topicLabel: item.headerSubtitle || "",
      questionAid: bm.canonicalAid ?? null,
      benchmarkEntry:
        bm.note != null
          ? { value: bm.note, count: 0, canonicalAid: bm.canonicalAid ?? "", sector: bm.sector ?? null }
          : null
    }
  }

  //Load survey data on load or when filters or params are changes
  useEffect(() => {
    async function load() {
      store.dispatch(filterStatus(STATUS_LOADING))

      //Filtername to use for the query
      const filterName = currentFilter.default ? currentFilter.id : currentFilter.name

      //Init show compnay
      const showCompany = props.isGollac
        ? false
        : props.filter.observerAttributes.length === 0 &&
          filterName !== "company" &&
          session.accountOptions.heatmapDisplayCompany

      const showPopulation = props.isGollac
        ? false
        : (!isEqual(new DashboardFilters(props.filter.dashboard), new DashboardFilters()) &&
            session.userRole === "ADMIN" &&
            filterName !== "company") ||
          (props.filter.observerAttributes.length > 0 && filterName !== "company")

      setIsLoading(true)
      setShowCompany(showCompany)
      setShowPopulation(showPopulation)

      //Fetch data with currentFilter et currentResultsType as params
      //Different route if data is generate for demonstration dashboard
      const response: any = props.survey.active.randomData
        ? await store.dispatch(
            generateHeatmap(
              props.survey.active.template.id,
              props.survey.active.isTest ? props.survey.active.id : null,
              filterName,
              props.filter.dashboard
            )
          )
        : await store.dispatch(
            fetchHeatmap(
              filterName,
              currentResultsType,
              session.userInitialLanguage,
              session.accountOptions.heatmapDisplayNoScore,
              session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction",
              props.filter.dashboard,
              getHeatmapCeils(filterName),
              session.dashboardDisplaySettings.hideStartedForParticipation,
              showCompany,
              showPopulation,
              props.isGollac ? props.isGollac : false,
              session.accountOptions.gollacManagerAttributeName,
              session.accountOptions.ceilsGollacMinValue,
              session.accountOptions.ceilsGollacMaxValue,
              undefined,
              undefined,
              props.isReport ?? false
            )
          )

      const showBenchmarkCol =
        canSeeBenchmark &&
        !props.isGollac &&
        !props.survey.active.randomData &&
        localStorage.getItem("benchmark_visible") !== "false"
      if (showBenchmarkCol && !bmResponseRef.current) {
        const fetched: any = await store.dispatch(benchmarkFetchQuestions())
        if (!fetched.error) bmResponseRef.current = fetched
      }
      const bmResponse: any = (showBenchmarkCol && bmResponseRef.current) || { error: "skipped" }

      //Detect if participation count is ok
      if (response.error === INSUFFICIENT_PARTICIPATION) {
        store.dispatch(filterStatus(INSUFFICIENT_PARTICIPATION))
      } else {
        store.dispatch(filterStatus(!response.error ? STATUS_LOADED : STATUS_LOAD_ERROR))
      }

      if (!response.error) {
        // Build benchmark lookup: app question UUID → { value, count, canonicalAid }
        const bmByUuid: Record<string, BenchmarkEntry> = {}
        if (!bmResponse.error) Object.assign(bmByUuid, buildBenchmarkByUuid(bmResponse))
        // bmByUuid is keyed by app question UUID across the WHOLE app (aidMap has no account
        // filter), so it's non-empty for virtually every account regardless of whether THIS
        // account's own questions have any match. Check against this account's actual questions
        // (topic.list) instead — otherwise the column shows up with every row as "—" for
        // accounts whose question wording isn't shared by any other account (see LISI).
        const hasBenchmark =
          showBenchmarkCol &&
          !bmResponse.error &&
          currentResultsType === "questions" &&
          // Level 3 gets the column even when nothing matches yet, that is where the dashes to
          // create a benchmark are
          (canCreateBenchmark || flatten(props.topic.list.map((tp) => tp.Questions)).some((q) => bmByUuid[q.id] != null))

        //Detect if the first column should be hidden
        let hideFirstColumn = false
        if (props.filter.dashboard.customFilters.length === 1) {
          hideFirstColumn = props.filter.dashboard.customFilters[0].id === filterName
        }

        //Make headers for the heatmap
        //For default filters : search translation
        //For customer filter : original name for reporting data / translation for demo data
        const newColumns: string[] = []
        response.columns.slice(FIRST_COLUMN_INDEX).forEach((column) => {
          newColumns.push(
            currentFilter.default
              ? currentFilter.id === "gender"
                ? t("user_" + currentFilter.id + "_" + column[1])
                : currentFilter.id === "company"
                  ? props.filter.observerAttributes.length > 0
                    ? props.filter.observerAttributes.map((x) => x.name).join(", ")
                    : session.accountName
                  : getUserFilterDates(
                      currentFilter.id === "birth_date" ? "BIRTH_DATE" : "COMPANY_WELCOME_DATE",
                      props.survey.active.dateStart
                    ).find((x: UserFilterDates) => x.id === column[1])?.name
              : props.survey.active.randomData
                ? t("filter_random_" + column[1])
                : column[1]
          )
        })

        if (response.population && !hideFirstColumn) {
          const attributesAids = flatten(props.filter.dashboard.customFilters.map((x) => x.attributesIds))
          const attributesNames = props.attribute.list.filter((x) => attributesAids.indexOf(x.id) > -1)
          if (attributesNames.length > 0) {
            newColumns.push(attributesNames.map((x) => x.name).join(", "))
          } else if (props.filter.observerAttributes.length > 0) {
            newColumns.push(props.filter.observerAttributes.map((x) => x.name).join(", "))
          }

          if (currentResultsType === "axes") {
            response.population.forEach((item) => {
              item.aid = axisFindTopicAid(item.aid)
            })
          }
        }

        //Add column for the company
        //If axis update the aid
        if (response.company) {
          newColumns.push(session.accountName)
          if (currentResultsType === "axes") {
            response.company.forEach((item) => {
              item.aid = axisFindTopicAid(item.aid)
            })
          }
        }

        // Append benchmark column header
        if (hasBenchmark) {
          const sectorLbl = bmResponse.sector ? t("benchmark_sector_" + bmResponse.sector) : "global"
          newColumns.push("__benchmark__")
          setBenchmarkSectorLabel(sectorLbl)
        }

        //Get all questions (in order to match questions if results type is question)
        const questions = flatten(props.topic.list.map((x) => x.Questions))

        //If randomize data and type === axes or survey
        if (props.survey.active.randomData && response.data[0].length) {
          //Init columns
          const randomDataColumns = response.data[0].slice(1)

          //> calculate mean for every axes from the topics
          if (currentResultsType === "axes") {
            const axes = props.axis.list
            const axesData: any = []
            let axisValues: any[] = []
            axes.forEach((axis) => {
              //Get all data for each list of topics for the axis
              const topicAxes = props.topic.list.filter((x) => x.AxisId === axis.id).map((x) => x.id)
              const topicAxesData = response.data.filter((x) => topicAxes.indexOf(x[0]) > -1)

              //Init array of values
              axisValues = new Array(randomDataColumns.length)

              //Push values in array
              randomDataColumns.forEach((col, i) => {
                axisValues[i] = []
                topicAxesData.forEach((line) => {
                  axisValues[i].push(line[i + 1])
                })
              })

              //Get mean and add to array
              axisValues = axisValues.map((x) => (x = mean(x)))
              axesData.push([topicAxes[0]].concat(axisValues))
            })

            response.data = response.data.slice(0, 3).concat(axesData)
          }

          //> calculate global mean for every population
          if (currentResultsType === "survey") {
            const surveyValues: any[] = []

            //Init values with empty array
            randomDataColumns.forEach((col, i) => {
              surveyValues[i] = []
            })

            //Add all values by columns in order to calculate mean
            response.data.slice(2).forEach((line) => {
              line.slice(1).forEach((item, i) => {
                surveyValues[i].push(item)
              })
            })

            //Calculate mean
            const surveyValuesMeans: any[] = [response.data[2][0]]
            surveyValues.forEach((values) => {
              surveyValuesMeans.push(mean(values))
            })

            //Erase data values
            response.data = response.data.slice(0, 2).concat([surveyValuesMeans])
          }
        }

        //Get participation for attributes
        const newParticipations: any = []
        if (response.data[0]) {
          //Get count of users that have participate (first row)
          response.data[0].slice(FIRST_COLUMN_INDEX).forEach((item) => {
            newParticipations.push({
              done: item
            })
          })

          //Get count of the total (second row)
          //Calculate participation
          if (response.data[1]) {
            response.data[1].slice(FIRST_COLUMN_INDEX).forEach((item, i) => {
              newParticipations[i].total = item
            })
          }
        }

        //Add participation for the selected filter
        if (response.populationParticipation && !hideFirstColumn) {
          const populationParticipation = response.populationParticipation
          newParticipations.push({
            done: session.dashboardDisplaySettings.hideStartedForParticipation
              ? populationParticipation.participation_done
              : populationParticipation.participation_done + populationParticipation.participation_started,
            total: populationParticipation.participation_send
          })
        }

        //Add company participation results
        if (response.companyParticipation) {
          const companyParticipation = response.companyParticipation
          newParticipations.push({
            done: session.dashboardDisplaySettings.hideStartedForParticipation
              ? companyParticipation.participation_done
              : companyParticipation.participation_done + companyParticipation.participation_started,
            total: companyParticipation.participation_send
          })
        }

        // Benchmark column has no participation data
        if (hasBenchmark) {
          newParticipations.push({ done: null, total: null, ratio: null, isBenchmark: true })
        }

        //Update ratio
        newParticipations.forEach((item) => {
          if (!item.isBenchmark) {
            item.ratio = Math.round((item.done / item.total) * 100)
          }
        })

        //Update item in store
        setParticipations(newParticipations)

        // Format array data that will be displayed
        // Start at row 2 (FIRST_LINE_INDEX)
        const data: any = []

        response.data.slice(!props.isGollac ? FIRST_LINE_INDEX : 0).forEach((item) => {
          //Init label and header
          let axisIndex = -1
          let topicOrder = 0
          let label = ""
          let headerLabel = ""
          let headerColor = ""
          let headerSubtitle = ""
          let liveNote: number | null = null
          let questionUuid: string | null = null

          //Skip row (for secondary questions)
          let skip = false

          //For axis edit aid to match with a topic
          if (currentResultsType === "axes") {
            item[0] = axisFindTopicAid(item[0])
          }

          //Id is the first item of the row
          const aid = item[0]

          //If aid === 0 it is company name
          if (aid === 0) {
            label = props.survey.active.name
          }

          //Get all values for one line
          const values: any[] = item.slice(FIRST_COLUMN_INDEX)

          //Get value for the filter
          if (response.population && !hideFirstColumn) {
            const populationItem = response.population.find((x) => x.aid === aid)
            const populationValue = populationItem ? populationItem.note : null
            values.push(populationValue)
          }

          //Get value for the company
          if (response.company) {
            const companyItem = response.company.find((x) => x.aid === aid)
            const companyValue = companyItem ? companyItem.note : null
            values.push(companyValue)
          }

          //Determine label
          //If survey name of the survey
          let topic
          if (currentResultsType === "survey") {
            label = props.survey.active.name
          }
          //For axis : search axis from the topic
          //For topic get label of the topic
          else {
            if (currentResultsType === "questions") {
              topic = props.topic.list.find((x) => x.Questions.map((x) => x.aid).indexOf(aid) > -1)
            } else {
              topic = props.topic.list.find((x) => (props.survey.active.randomData ? x.id : x.aid) === aid)
            }

            if (topic) {
              const axis = topic.Axis ? new Axis(topic.Axis) : null
              axisIndex = axis ? props.axis.list.findIndex((x) => x.id === axis.id) : 99
              topicOrder = props.topic.list.findIndex((x) => x.id === topic.id)

              //Define label
              if (currentResultsType === "axes") {
                label = axis ? axis.label : t("questions_without_axis")
              } else if (currentResultsType === "topics") {
                label = topic.label
              } else if (topic.Questions.length) {
                const question = questions.find((x) => x.aid === aid)
                if (question) {
                  label = question.label
                  skip = question.primaryId !== null
                  liveNote = question.note
                  questionUuid = question.id
                } else {
                  label = ""
                  skip = true
                }
              }

              //Define header
              if (currentResultsType === "topics" || currentResultsType === "questions") {
                headerLabel = axis ? axis.label : ""
                headerSubtitle = currentResultsType === "questions" ? topic.label : ""
                headerColor = axis ? axis.color : ""
              }
            }
          }

          //Set label for eacg line and init value array
          const line: any = {
            axisIndex,
            topicOrder,
            headerLabel,
            headerColor,
            headerSubtitle,
            label,
            liveNote,
            questionUuid,
            //The bank is matched on French texts
            questionTextFr: questionUuid
              ? flatten(props.topic.list.map((tp) => tp.Questions)).find((q) => q.id === questionUuid)?.name?.fr || label
              : null,
            values: []
          }

          //Format data for the heatmap
          //if value is less than 10 add one decimal
          values.forEach((value, i) => {
            let label = null
            if (value !== null && value !== undefined) {
              label =
                session.dashboardDisplaySettings.dashboardDisplayMode === "note"
                  ? (value / 10).toFixed(value < 100 ? 1 : 0)
                  : value.toFixed(0)
            }

            // Initialize the object to push
            const dataObject: {
              note: number | null
              label: string | null
              gollacField?: string
            } = {
              note: value,
              label
            }

            // Add gollacField only if props.isGollac is true
            if (props.isGollac) {
              switch (i) {
                case 0:
                  dataObject.gollacField = "employee"
                  break
                case 1:
                  dataObject.gollacField = "gap"
                  // Assign note from index 3 to dataObject.note and move current note to label
                  dataObject.label = dataObject.note ? dataObject.note.toString() : null
                  dataObject.note = values[3] ?? null // Assign value from index 3
                  break
                case 2:
                  dataObject.gollacField = "worst"
                  // store gap classification to color this cell
                  ;(dataObject as any).gapLabel = values[1] ?? null
                  break
                case 3:
                  return
              }
            }

            line.values.push(dataObject)
          })

          //Add company value (in order to sort array and to compare for the color)
          line.companyValue = line.values[line.values.length - 1].note

          //Custom color mode
          //Set color according values
          const notes = line.values.map((x) => x.note)
          line.values.forEach((value) => {
            const note = value.note

            if (note >= session.dashboardDisplaySettings.dashboardNightmareModeMax) {
              value.color =
                max(notes) === note && !session.dashboardDisplaySettings.dashboardNightmareModeHeatmap
                  ? "#51e898"
                  : colorGreen
            } else if (note < session.dashboardDisplaySettings.dashboardNightmareModeMin) {
              value.color =
                min(notes) === note && !session.dashboardDisplaySettings.dashboardNightmareModeHeatmap
                  ? "#f05146"
                  : colorRed
            } else {
              //Detect if color is grayscale
              //If it is the case do not apply color
              if (session.dashboardDisplaySettings.dashboardNightmareModeHeatmap) {
                const color = session.dashboardDisplaySettings.dashboardNightmareColor2
                color.slice(1)
                const colorBlocks: string[] = []
                for (let i = 1; i < color.length - 1; i++) {
                  if (i % 2 === 1) {
                    colorBlocks.push(color[i] + color[i + 1])
                  }
                }

                if (uniq(colorBlocks).length > 1) {
                  value.color = session.dashboardDisplaySettings.dashboardNightmareColor2
                }
              }
            }
          })

          // Append benchmark value after regular color computation
          if (hasBenchmark) {
            //hasBenchmark already implies currentResultsType === "questions"
            const bmEntry = questionUuid ? (bmByUuid[questionUuid] ?? null) : null
            const bmValue: number | null = bmEntry?.value ?? null
            const bmCanonicalAid: string | null = bmEntry?.canonicalAid ?? null
            const bmSector: string | null = bmEntry?.sector ?? null
            const bmLabel =
              bmValue !== null
                ? session.dashboardDisplaySettings.dashboardDisplayMode === "note"
                  ? (bmValue / 10).toFixed(bmValue < 100 ? 1 : 0)
                  : bmValue.toFixed(0)
                : null
            const bmDataObject: any = {
              note: bmValue,
              label: bmLabel,
              isBenchmark: true,
              canonicalAid: bmCanonicalAid,
              sector: bmSector
            }
            if (bmValue !== null) {
              if (bmValue >= session.dashboardDisplaySettings.dashboardNightmareModeMax) {
                bmDataObject.color = colorGreen
              } else if (bmValue < session.dashboardDisplaySettings.dashboardNightmareModeMin) {
                bmDataObject.color = colorRed
              }
            }
            line.values.push(bmDataObject)
          }

          if (!skip) {
            data.push(line)
          }
        })

        //Update state
        if (newColumns.length > 10) {
          setFirstColumnWidth(320)
        } else if (newColumns.length > 15) {
          setFirstColumnWidth(280)
        } else {
          setFirstColumnWidth(400)
        }

        setShowBenchmark(hasBenchmark)

        if (props.isGollac) {
          const translateColsName: string[] = newColumns.map((col: string) => t(col))
          setFirstColumnWidth(280)
          setColumns(translateColsName)
        } else {
          if (newColumns.length > 25) {
            setScale(0.78)
          }
          setColumns(newColumns)
        }

        // setColumns(newColumns);
        setColumnsTextLength(Math.max(...newColumns.map((x) => x.length)))
        setData(
          !props.isGollac
            ? orderBy(
                data.filter((x) => x.label !== ""),
                ["axisIndex", "topicOrder", "headerColor", "headerSubtitle", "label"]
              )
            : data
        )

        store.dispatch(reportEdit("heatmapLength", data.length))
        //Reset params
        window.history.pushState({}, "", window.location.pathname)
      }

      setIsLoading(false)
    }

    load()
    setCurrentModal(null)

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filter.dashboard, currentFilter, currentResultsType])

  //Click column to display tree
  //If default attribute search attribute aid
  async function clickColumn(column) {
    if (!props._session.isTabDisplayed("/dashboard/overview")) return false

    const dashboardFilters = new DashboardFilters()

    //Behavior for default filters
    //Find id from the matching configuration list
    if (currentFilter.default) {
      if (currentFilter.id === "gender") {
        const gender = USER_LIST_GENDER.find((x) => t(x.name) === column)
        dashboardFilters.gender = gender ? gender.id : null
      } else if (currentFilter.id === "birth_date") {
        const birthDate: UserFilterDates | undefined = getUserFilterDates(
          "BIRTH_DATE",
          props.survey.active.dateStart
        ).find((x: UserFilterDates) => t(x.name) === column)
        dashboardFilters.birthDate = birthDate ? birthDate.id : null
      } else if (currentFilter.id === "company_welcome_date") {
        const companyWelcomeDate: UserFilterDates | undefined = getUserFilterDates(
          "COMPANY_WELCOME_DATE",
          props.survey.active.dateStart
        ).find((x: UserFilterDates) => t(x.name) === column)
        dashboardFilters.companyWelcomeDate = companyWelcomeDate ? companyWelcomeDate.id : null
      }
    }
    //For custom filter
    else {
      //Search matching attribute for random data according the name
      if (props.survey.active.randomData) {
        const attribute = DEFAULT_RANDOM_ATTRIBUTES.find((x) => t(x.name) === column)
        if (attribute) {
          dashboardFilters.customFilters = [new FilterLite({ id: "random", attributesIds: [attribute.id] })]
        }
      }
      //Search in the list of attributes from the database
      else {
        //Search filterId
        const searchedFilter = props.filter.list.find((x) => x.name === currentFilter.name)
        if (searchedFilter) {
          setCurrentModal(MODAL_LOADING)
          const attributesResponse: any = await store.dispatch(
            fetchAttributes(new DashboardFilters(), currentFilter.name)
          )
          if (!attributesResponse.error) {
            const attribute = attributesResponse.find((x) => x.name === column)
            if (attribute) {
              dashboardFilters.customFilters = [
                new FilterLite({
                  id: searchedFilter.id,
                  attributesIds: [attribute.aid.toString()]
                })
              ]
            } else {
              setCurrentModal(null)
            }
          }
        }
      }
    }

    //Redirect
    setTimeout(() => {
      store.dispatch(filterReplaceDashboard(dashboardFilters))
      navigate("/dashboard/overview")
    }, 200)
  }

  //Get classname for a cell
  function getCellClassName(index, isParticipation) {
    let className = "heatmap-cell rel flex flex-dcol"

    if (isParticipation) {
      className += " grey-t"
    }

    // Offset by 1 when benchmark column is appended (benchmark has its own separator)
    const bmOffset = showBenchmark ? 1 : 0

    if (showCompany && showPopulation && index === columns.length - 2 - bmOffset) {
      className += " heatmap-cell-company"
    }

    if (!showPopulation && showCompany && index === columns.length - 1 - bmOffset) {
      className += " heatmap-cell-company"
    }

    if (showPopulation && !showCompany && index === columns.length - 1 - bmOffset) {
      className += " heatmap-cell-company"
    }

    return className
  }

  //Classname depend if bookmark are defined
  function getClassName() {
    let className = "heatmap-container"
    if (props.isGollac) {
      className += " heatmap-gollac"
    } else {
      if (props.bookmark.list.length > 0) {
        className += " heatmap-container-bookmark"
      }
    }

    return className
  }

  //Get style for the column
  function getColumnStyle(index) {
    let marginLeft

    const bmOffset = showBenchmark ? 1 : 0

    if (showCompany && showPopulation && index === columns.length - 2 - bmOffset) {
      marginLeft = 60
    }

    if (!showPopulation && showCompany && index === columns.length - 1 - bmOffset) {
      marginLeft = 60
    }

    if (showPopulation && !showCompany && index === columns.length - 1 - bmOffset) {
      marginLeft = 60
    }

    return {
      marginLeft
    }
  }

  //Get all rows
  //Apply restriction if preview
  function getRows() {
    if (props.isPreview && data.length) {
      const offset = props.offset ? props.offset + 1 : 1
      const limit = props.limit ? props.limit : 0

      // Skip first row if requested (for pagination on subsequent pages)
      if (props.skipFirstRow) {
        return data.slice(offset, offset + limit)
      }
      return [data[0]].concat(data.slice(offset, offset + limit))
    } else {
      return data
        .filter((item) => {
          const searchValue = searchTerm.toLowerCase()
          return (
            item.label.toLowerCase().includes(searchValue) ||
            item.headerSubtitle?.toLowerCase().includes(searchValue) ||
            item.headerLabel?.toLowerCase().includes(searchValue)
          )
        })
        .map((item) => ({
          ...item,
          label: highlightText(item.label, searchTerm),
          headerSubtitle: highlightText(item.headerSubtitle ?? "", searchTerm),
          headerLabel: highlightText(item.headerLabel ?? "", searchTerm)
        }))
    }
  }

  // sorting columns
  const handleSort = (isNumeric: boolean = false, index?: number | string) => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"))

    // Set the active column index for highlighting the clicked column
    if (index !== undefined) {
      setActiveColumnIndex(index)
    }

    // Ignore the first item for sorting
    const sorted = [
      data[0],
      ...data.slice(1).sort((a, b) => {
        let aValue: number | string, bValue: number | string

        // Sorting by topicOrder
        aValue = a.topicOrder
        bValue = b.topicOrder

        // If sorting by numeric values
        if (isNumeric && index !== undefined) {
          aValue = a.values[index].note
          bValue = b.values[index].note
        }

        // Sorting logic based on the current sortOrder
        return sortOrder === "asc" ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1
      })
    ]

    setData(sorted)
  }

  // highlight searching text
  function highlightText(text: string, searchTerm: string) {
    if (!searchTerm) return text
    const regex = new RegExp(`(${searchTerm})`, "gi")
    const parts = text.split(regex)

    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={index} style={{ backgroundColor: "yellow" }}>
          {part}
        </span>
      ) : (
        part
      )
    )
  }

  //Init filters
  function initCurrentFilter() {
    if (props.survey.active.randomData) {
      return new Filter(initFilters().reverse()[0])
    } else {
      const filterName: string =
        props.isGollac && session.accountOptions.gollacFilterName
          ? session.accountOptions.gollacFilterName
          : props.filterName
            ? props.filterName
            : session.accountOptions.heatmapFilterName
              ? session.accountOptions.heatmapFilterName
              : "company"

      const customFilter: Filter | undefined = props.filter.list.find((x: Filter) => x.name === filterName)

      const defaultFilter: any | undefined = DEFAULT_FILTERS.find((x) => x.id === filterName)

      const companyFilter: Filter = new Filter({
        id: "company",
        name: "user_company_alt",
        default: true
      })

      if (defaultFilter) {
        return new Filter(defaultFilter)
      } else if (customFilter) {
        return new Filter(customFilter)
      } else {
        return companyFilter
      }
    }
  }

  //Init results type
  function initCurrentResultsType() {
    const currentResultsType: AccountHeatmapResult | undefined = props.resultsType
      ? ACCOUNT_RESULTS_TYPES.find((x: AccountHeatmapResult) => x === props.resultsType)
      : undefined
    if (currentResultsType) {
      return currentResultsType
    } else {
      if (props.isGollac) {
        return session.accountOptions.gollacHeatmapViewDefault
      } else {
        return session.accountOptions.heatmapResultsType
          ? session.accountOptions.heatmapResultsType
          : ACCOUNT_RESULTS_TYPES[0]
      }
    }
  }

  //Is dot visible
  function isDotVisible() {
    const noFilter = isEqual(new DashboardFilters(props.filter.dashboard), new DashboardFilters())
    return !noFilter
  }

  //Select filter to display
  function selectFilter(filter: Filter) {
    setCurrentFilter(filter)
  }

  //Select type of results to display
  function selectResultsType(resultsType: AccountHeatmapResult) {
    setData([])
    setCurrentResultsType(resultsType)
  }

  function ResetFilters() {
    return (
      <>
        <PagePlaceholder title={t("dashboard_heatmap_empty")} image="sherlock" />
        <div className="flex justify-center">
          <Button isWithBorder isFullWidth className="primary" onClick={() => setCurrentFilter(initCurrentFilter())}>
            {t("filter_init")}
          </Button>
        </div>
      </>
    )
  }

  return (
    <div>
      {benchmarkCreateQuestion && (
        <QuestionBenchmarkModal
          questionId={benchmarkCreateQuestion.id}
          text={benchmarkCreateQuestion.text}
          onClose={() => setBenchmarkCreateQuestion(null)}
        />
      )}

      {hmDetailOpen && hmDetailData && (
        <BenchmarkDetailModal
          accountName={props._session.accountName}
          accountOptions={props._session.dashboardDisplaySettings}
          score={hmDetailData.score}
          axisColor={hmDetailData.axisColor}
          questionAid={hmDetailData.questionAid}
          benchmarkEntry={hmDetailData.benchmarkEntry}
          // Not benchmarkSector (the account's own sector) — see buildBenchmarkByUuid: this
          // question may have fallen back to the all/all row, so scope to the entry's own sector.
          sector={hmDetailData.benchmarkEntry?.sector ?? null}
          navItems={hmNavItems}
          navCurrentIndex={hmNavIndex}
          onNavigate={hmNavigateTo}
          onClose={() => setHmDetailOpen(false)}
        />
      )}

      {currentModal === MODAL_LOADING && <LoadingModal />}

      {currentModal === MODAL_HELP && (
        <HeatmapHelp
          onClose={() => setCurrentModal(null)}
          resultsTypes={ACCOUNT_RESULTS_TYPES.map((x: AccountHeatmapResult) => ({ id: x, name: x }))}
        />
      )}

      {currentModal === MODAL_FILTERS && (
        <Modal isCloseButtonVisible title={t("filters")} onClose={() => setCurrentModal(null)}>
          <div
            style={{
              marginRight: "160px",
              marginBottom: "290px"
            }}>
            <DashboardFiltersWidget currentFilters={props.filter.list} currentSurvey={props.survey.active} />
          </div>
        </Modal>
      )}

      {props.filter.status === INSUFFICIENT_PARTICIPATION ? (
        <div className={`${!props.isGollac ? "w-dvw" : ""}`}>
          <NoResultsWidget />
          {!isEqual(new DashboardFilters(props.filter.dashboard), new DashboardFilters()) && (
            <div className="flex">
              <Space />
              <Button isWithBorder className="primary" onClick={() => setCurrentModal(MODAL_FILTERS)}>
                {t("filter_reset")}
              </Button>
              <Button
                isWithBorder
                className="secondary"
                onClick={() => store.dispatch(filterReplaceDashboard(new DashboardFilters()))}>
                {t("utils_back")}
              </Button>
              <Space />
            </div>
          )}
        </div>
      ) : isLoading && !props.isGollac ? (
        <div className="flex1 header-loader">
          <PageLoader status={STATUS_LOADING} />
        </div>
      ) : (
        <div className={props.isPreview ? "" : getClassName()}>
          <Card
            isNotCardClass={props.isGollac ? true : undefined}
            isWithoutPadding
            exportName={t(currentFilter.name)}
            isOverflowHidden={!props.isPreview}>
            <table
              id={props.id ? props.id : "heatmap-table"}
              style={{
                transform: "scale(" + scale + ")",
                transformOrigin: "top left",
                width: props.isGollac ? "100%" : "",
                overflow: props.isGollac ? "hidden" : undefined
              }}>
              <thead
                className={"heatmap-header " + (props.isPreview ? "" : "lightwhite")}
                style={{
                  display: data.length && !props.hideHeader ? "block" : "none",
                  marginTop: props.bookmark.list.length > 1 ? 43 : 0
                }}>
                <tr className="flex" style={{ height: 130, paddingLeft: 32 }}>
                  <th
                    scope="col"
                    className="flex flex-dcol"
                    style={{
                      padding: "24px 8px",
                      minWidth: firstColumnWidth,
                      fontWeight: "initial",
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 3
                    }}>
                    <div className="dashboard-heatmap-separator" />

                    {!props.isPreview && !props.isGollac && (
                      <div className="abs lightwhite flex" style={{ padding: 4 }}>
                        <ListButton onClick={() => setCurrentModal(MODAL_HELP)} text={t("utils_params")} icon={faCog} />
                        <ListButton
                          dot={isDotVisible()}
                          onClick={() => setCurrentModal(MODAL_FILTERS)}
                          text={t("filters")}
                          icon={faFilter}
                        />
                      </div>
                    )}

                    <div className="flex">
                      <Space />
                      <div
                        className="dashboard-heatmap-filter-name"
                        id={currentFilter.default ? currentFilter.id : currentFilter.name}>
                        {props.isPreview && !props.isGollac ? (
                          <div style={{ fontWeight: 700 }}>
                            {t(currentFilter.default ? "user_" + currentFilter.id + "_alt" : currentFilter.name)}
                          </div>
                        ) : !props.isGollac ? (
                          <ListDropdown
                            active={currentFilter.id}
                            isBold
                            isOutlined
                            onSelect={selectFilter}
                            value={t(currentFilter.default ? "user_" + currentFilter.id + "_alt" : currentFilter.name)}
                            values={filters}
                          />
                        ) : null}
                      </div>
                    </div>

                    <Space />

                    <div className="">
                      <div
                        className="dashboard-heatmap-results-type flex1 flex flex-dcol"
                        style={{ textAlign: "left" }}
                        id={currentResultsType}>
                        <Space />
                        {props.isPreview ? (
                          <div style={{ fontWeight: 700 }}>{t(currentResultsType)}</div>
                        ) : (
                          <ListDropdown
                            active={currentResultsType}
                            isBold
                            isOutlined
                            onSelect={(type: any) => selectResultsType(type.id)}
                            value={t(currentResultsType)}
                            values={ACCOUNT_RESULTS_TYPES.map((x: AccountHeatmapResult) => ({ id: x, name: x }))}
                          />
                        )}
                      </div>
                    </div>
                  </th>

                  {
                    // display all columns (attributes start at 1)
                    data.length > 0 &&
                      columns.map((column, c) => {
                        const isBmCol = showBenchmark && column === "__benchmark__"
                        return (
                          <th
                            scope="col"
                            key={c}
                            className={
                              !props.isGollac
                                ? "heatmap-column heatmap-cell"
                                : "heatmap-column heatmap-cell heatmap-cell-company"
                            }
                            style={{
                              ...getColumnStyle(c),
                              fontWeight: "initial",
                              ...(isBmCol ? { marginLeft: 16 } : {}),
                              display: column === "gollac_delta" || (props.isGollac && c < 2) ? "none" : "flex"
                            }}>
                            <div className="heatmap-column-text flex">
                              {isBmCol ? (
                                <span style={{ fontSize: columnsTextLength > 32 ? 10 : 12 }}>
                                  <FontAwesomeIcon icon={faMagnifyingGlassChart} />{" "}
                                  {benchmarkSectorLabel}
                                </span>
                              ) : (
                                <span
                                  className="_hover"
                                  style={{ fontSize: columnsTextLength > 32 ? 10 : 12 }}
                                  onClick={() => {
                                    if (!props.isGollac) clickColumn(column)
                                  }}>
                                  {props.isGollac && c === 2 ? t("gollac_heatmap_gap_header") : column}
                                </span>
                              )}
                            </div>
                          </th>
                        )
                      })
                  }

                  <th scope="col" className="heatmap-column heatmap-cell" />
                </tr>
              </thead>

              {data.length > 0 ? (
                <tbody
                  className="heatmap-data"
                  style={{
                    backgroundColor: props.isPreview ? undefined : "white",
                    display: "block"
                  }}>
                  {!props.isPreview && (
                    <tr
                      style={{
                        paddingTop: "inherit",
                        paddingBottom: "inherit"
                      }}
                      className={"flex flex1 list-item"}>
                      <th
                        scope="row"
                        style={{
                          textAlign: "left",
                          position: "sticky",
                          left: 0,
                          backgroundColor: props.isPreview ? undefined : "white",
                          zIndex: 1,
                          fontWeight: "initial",
                          width: firstColumnWidth,
                          padding: "0px 8px",
                          display: "flex",
                          alignItems: "center"
                        }}>
                        <input
                          className="max-w-sm border-x-0 border-t-0 border-b-[1px] text-gray-700 mr-3 py-1 px-2 leading-tight focus:outline-none focus:border-idt-primary-main"
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder={`${t("utils_search")}...`}
                        />

                        <div className="_hover ml-auto" onClick={() => handleSort(false, "firstCol")}>
                          <FontAwesomeIcon
                            icon={faUndo}
                            style={{
                              fontSize: "13px",
                              padding: 5,
                              cursor: "pointer"
                            }}
                          />
                        </div>
                      </th>

                      {
                        // display all columns to sort
                        data.length > 0 &&
                          columns.map((column, c) => {
                            // In gollac mode, only show sort for the worst column (index 2)
                            if (props.isGollac && c !== 2) return null
                            // No sort for benchmark column
                            if (showBenchmark && column === "__benchmark__") return null

                            return (
                              <th
                                key={`sort-index-${c}`}
                                scope="row"
                                className={!props.isGollac ? "" : "heatmap-column heatmap-cell heatmap-cell-company"}
                                style={{
                                  display: "flex",
                                  alignItems: "center"
                                }}>
                                <div
                                  className={`${
                                    activeColumnIndex === c ? "text-idt-primary-main" : "text-idt-info-main"
                                  } _hover`}
                                  style={{
                                    maxWidth: 52,
                                    minWidth: "52px",
                                    textAlign: "center",
                                    marginLeft:
                                      getCellClassName(c, false) ===
                                      "heatmap-cell rel flex flex-dcol heatmap-cell-company"
                                        ? "60px"
                                        : undefined
                                  }}
                                  onClick={() => handleSort(true, c)}>
                                  <FontAwesomeIcon
                                    icon={
                                      sortOrder === "asc" && activeColumnIndex === c
                                        ? faSortAmountUp
                                        : faSortAmountDownAlt
                                    }
                                    style={{
                                      fontSize: "13px",
                                      padding: 5,
                                      cursor: "pointer"
                                    }}
                                  />
                                </div>
                              </th>
                            )
                          })
                      }
                    </tr>
                  )}

                  {
                    /** loop on each row */
                    getRows().map((item: any, i: number) => (
                      <tr
                        key={i}
                        style={{ borderTop: i <= 1 ? "1px solid white" : undefined, padding: "6px 32px" }}
                        className={
                          "flex flex1 list-item " +
                          (props.isPreview && item.headerLabel.length > 0 ? "" : "list-item-large")
                        }>
                        <th
                          scope="row"
                          style={{
                            textAlign: "left",
                            position: "sticky",
                            left: 0,
                            backgroundColor: props.isPreview ? undefined : "white",
                            zIndex: 1,
                            fontWeight: "initial",
                            width: firstColumnWidth,
                            padding: "3px 8px"
                          }}>
                          <SurveyQuestionLabel
                            topicLabel={item.headerSubtitle}
                            axisLabel={item.headerLabel}
                            color={item.headerColor}
                            label={i === 0 && !props.isGollac && searchTerm === "" ? <b>{item.label}</b> : item.label}
                          />
                        </th>

                        {item.values.map((value: any, j: number) => (
                          <td
                            style={{
                              width: 50,
                              textAlign: props.isGollac ? "left" : undefined,
                              marginLeft: value.isBenchmark ? 28 : undefined,
                              display:
                                props.isGollac && (value.gollacField === "employee" || value.gollacField === "gap")
                                  ? "none"
                                  : undefined
                            }}
                            className={
                              !props.isGollac
                                ? getCellClassName(j, false)
                                : `${getCellClassName(j, false)} heatmap-cell-company`
                            }
                            key={j}>
                            <Space />

                            {value.isBenchmark ? (
                              value.note != null ? (
                                <div className="flex justify-center w-full">
                                  <DashboardBenchmarkMiniCard
                                    accountOptions={props._session.dashboardDisplaySettings}
                                    score={item.companyValue / 10}
                                    benchmarkValue={value.note / 10}
                                    showLabel={false}
                                    onOpen={() => {
                                      setHmDetailData(buildHmDetailData(item, value))
                                      setHmDetailOpen(true)
                                    }}
                                  />
                                </div>
                              ) : i === 0 && !props.isGollac && searchTerm === "" ? null : canCreateBenchmark &&
                                item.questionUuid ? (
                                // Level 3: the dash opens the bank modal (look for an existing question,
                                // else create it) instead of being a dead end.
                                <Tooltip text="Créer ou associer cette question à la banque" position="top" usePortal>
                                  <span
                                    className="grey-t _hover"
                                    style={{ cursor: "pointer", padding: "0 8px" }}
                                    onClick={() =>
                                      setBenchmarkCreateQuestion({ id: item.questionUuid, text: item.questionTextFr })
                                    }>
                                    —
                                  </span>
                                </Tooltip>
                              ) : (
                                // Top row is the survey's overall score, which has no benchmark: leave it empty
                                // instead of a dash (same condition as its bold label above).
                                <span className="grey-t">—</span>
                              )
                            ) : value.label ? (
                              value.color && !props.isGollac ? (
                                <div
                                  className="flex"
                                  style={{
                                    maxWidth: 52
                                  }}>
                                  <Space />
                                  <Chip color={value.color} onClick={() => setCurrentCellOpen([true, i, j])}>
                                    {value.label}
                                  </Chip>
                                  <Space />
                                </div>
                              ) : !props.isGollac ? (
                                <b className="grey-t _hover" onClick={() => setCurrentCellOpen([true, i, j])}>
                                  {value.label}
                                </b>
                              ) : // gollacCase
                              value.gollacField ===
                                "employee" ? null /* <Chip onClick={() => setCurrentCellOpen([true, i, j])}>{value.label}</Chip> */ : value.gollacField ===
                                "gap" ? null /* gap icon hidden */ : value.gollacField === "worst" ? (
                                <b
                                  style={{
                                    width: "140px",
                                    color:
                                      (value as any).gapLabel === "critical"
                                        ? "#eb5a46"
                                        : (value as any).gapLabel === "moderate"
                                          ? "#f2d600"
                                          : "#20CA7E"
                                  }}>
                                  {t(value.note)}
                                </b>
                              ) : null
                            ) : (
                              !props.isGollac && (
                                <div className="_hover">
                                  <FontAwesomeIcon
                                    icon={faBan}
                                    className="grey-t"
                                    onClick={() => setCurrentCellOpen([true, i, j])}
                                  />
                                </div>
                              )
                            )}

                            {
                              /** display help on clic on cell */
                              currentCellOpen[0] === true && currentCellOpen[1] === i && currentCellOpen[2] === j && (
                                <ContextMenu onClose={() => setCurrentCellOpen([false, 0, 0])}>
                                  <ListItem>
                                    {value.label
                                      ? t(
                                          "dashboard_heatmap_help_" +
                                            currentResultsType +
                                            "_" +
                                            session.dashboardDisplaySettings.dashboardDisplayMode,
                                          {
                                            note: value.label,
                                            attribute: columns[j],
                                            templateItem: item.label
                                          }
                                        )
                                      : t("survey_no_results_help", {
                                          count: session.participationMin
                                        })}
                                  </ListItem>
                                </ContextMenu>
                              )
                            }

                            <Space />
                          </td>
                        ))}

                        {i === 0 && searchTerm === "" && (
                          <td
                            style={{
                              height: 1,
                              borderBottom: "1px solid #8c8c8c",
                              width: "100%",
                              position: "absolute",
                              bottom: 0,
                              left: 0
                            }}
                          />
                        )}
                      </tr>
                    ))
                  }

                  {session.accountOptions.heatmapDisplayParticipation && !props.isGollac && (
                    <tr className="flex flex1 list-item list-item-large">
                      <th
                        scope="row"
                        style={{
                          textAlign: "left",
                          position: "sticky",
                          left: 0,
                          zIndex: 1,
                          backgroundColor: props.isPreview ? undefined : "white",
                          fontWeight: "initial",
                          width: firstColumnWidth,
                          padding: "3px 8px"
                        }}>
                        <b>{t("survey_participation")}</b>
                      </th>

                      {participations.map((item, i) => (
                        <td
                          className={getCellClassName(i, true)}
                          style={{ marginLeft: item.isBenchmark ? 16 : undefined }}
                          key={i}>
                          {item.isBenchmark ? null : (
                            <>
                              <span>
                                <b>{item.ratio}</b>%
                              </span>
                              <br />
                              <span style={{ fontSize: "10px" }}>
                                {item.done} / {item.total}
                              </span>
                            </>
                          )}
                        </td>
                      ))}
                    </tr>
                  )}
                </tbody>
              ) : (
                <tbody>
                  <tr>
                    <td>
                      {!props.isGollac ? (
                        <ResetFilters />
                      ) : props.isGollac && isLoading ? (
                        <PageLoader status={STATUS_LOADING} />
                      ) : (
                        <ResetFilters />
                      )}
                    </td>
                  </tr>
                </tbody>
              )}

              <tfoot className="height-60" />
            </table>
          </Card>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: StateProps) => ({
  _session: state._session,
  attribute: state.attribute,
  axis: state.axis,
  bookmark: state.bookmark,
  filter: state.filter,
  survey: state.survey,
  project: state.project,
  topic: state.topic
})

export default connect(mapStateToProps)(withTranslation()(DashboardHeatmapWidget))
